import type { InspectionRound } from "../domain/inspectionRounds";
import { FIELD_ROUND, roundReference } from "../domain/inspectionRounds";
import { INSPECTIONS } from "./inspections";
import {
  BUILDING_ITEMS,
  BUILDING_ROUND,
  LANDSIDE_ITEMS,
  LANDSIDE_ROUND,
  UTILITY_CHECK,
  UTILITY_ITEMS,
} from "../domain/boardChecklists";

/*
 * The sample's inspection rounds, in the platform's shapes.
 *
 * The checklists a round may be filed against, with what the platform checks
 * an answer against: the item keys of the airports pack's movement area round
 * (packs/airports/checklists/oema-field-round.yaml), the three answers each
 * takes, and that a finding asks for a photograph. Kept to what the sample
 * needs to refuse what the platform would refuse — not a copy of the pack.
 */
export interface SampleChecklist {
  name: string;
  options: readonly string[];
  /** the answer that is a finding */
  fails: string;
  /** a finding needs a photograph */
  photoOnFail: boolean;
  items: readonly string[];
}

export const SAMPLE_CHECKLISTS: Record<string, SampleChecklist> = {
  [FIELD_ROUND]: {
    name: "OEMA Movement Area Round",
    options: ["pass", "find", "skip"],
    fails: "find",
    photoOnFail: true,
    items: [
      "pavement",
      "rwy",
      "twy",
      "shoulder",
      "resa",
      "markings",
      "agl",
      "papi",
      "navaid",
      "signs",
      "friction",
      "drainage",
      "fod",
      "spill",
      "veg",
      "wildlife",
      "fence",
      "roads",
      "rff",
      "windsock",
      "safety",
    ],
  },
  /* build 7: the Inspection platform's other two scopes, and the daily utility check */
  [BUILDING_ROUND]: {
    name: "OEMA Building Round",
    options: ["pass", "find", "skip"],
    fails: "find",
    photoOnFail: false,
    items: BUILDING_ITEMS,
  },
  [LANDSIDE_ROUND]: {
    name: "OEMA Landside Round",
    options: ["pass", "find", "skip"],
    fails: "find",
    photoOnFail: false,
    items: LANDSIDE_ITEMS,
  },
  [UTILITY_CHECK]: {
    name: "OEMA Daily Utility Check",
    options: ["pass", "fail", "skip"],
    fails: "fail",
    photoOnFail: false,
    items: UTILITY_ITEMS,
  },
};

/** The sample's completed rounds: the sample inspections filed against a round checklist. */
export function seedRounds(): (InspectionRound & { siteId: string })[] {
  return INSPECTIONS.filter((i) => i.status === "completed" && SAMPLE_CHECKLISTS[i.template]).map((i) => ({
    siteId: i.siteId,
    id: i.id,
    reference: roundReference(i.id),
    template: i.template,
    templateName: i.templateName,
    status: i.status,
    startedAt: i.startedAt,
    completedAt: i.completedAt,
    score: i.score,
    capturedOffline: i.capturedOffline,
    attrs: {},
    answers: i.observations.map((o) => ({
      id: o.id,
      itemKey: o.itemKey,
      result: o.result,
      valueText: o.valueText,
      notes: o.notes,
      skippedReason: o.skippedReason,
      attrs: {},
      defectId: o.defectId,
      photos: o.photos,
    })),
    workOrders: [],
  }));
}
